import React from "react";
import { CoverImage as CoverImageType } from "@src/types.generated";
import AddMediaModal from "@src/components/modals/addMedia/AddMediaModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/ui/tooltip";
import { LinkType } from "@src/utils/files";
import { Button } from "@src/ui/button";
import BucketPaintOutlineIcon from "@src/ui/icons/18px/bucket-paint-outline";
import CropIcon from "@src/ui/icons/18px/crop";
import TrashOutlineIcon from "@src/ui/icons/18px/trash-outline";
import PinturaModal from "@src/components/ui/PinturaModal";

type Props = {
  coverImage: CoverImageType | null | undefined;
  setCoverImage: (coverImage: CoverImageType | undefined | null) => void;
};

const UpdateCoverImageMenu: React.FC<Props> = (props) => {
  const [showPinturaModal, setShowPinturaModal] = React.useState(false);
  const fullSizeCoverImage = React.useMemo(
    () =>
      props.coverImage?.imageUrls?.medium ||
      props.coverImage?.imageUrls?.original ||
      props.coverImage?.imageUrls?.wide,
    [props.coverImage],
  );
  return (
    <TooltipProvider>
      <div className="group absolute inset-0 flex items-end justify-end gap-2 p-2">
        <AddMediaModal
          title="Add media"
          onMediaChange={(x) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              emojiIcon: null,
              background: null,
              mediaUrl: x.mediaUrls.en,
              imageUrls: {
                id: props.coverImage?.id || "",
                medium: x.mediaUrls.en,
                wide: x.mediaUrls.en,
                original: x.mediaUrls.en || "",
              },
            });
          }}
          onResourceChange={(trainingResource) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              emojiIcon: null,
              background: null,
              mediaUrl: trainingResource.publishedVersion?.media?.mediaUrls.en,
              imageUrls: {
                id: props.coverImage?.id || "",
                medium: trainingResource.publishedVersion?.media?.mediaUrls.en,
                wide: trainingResource.publishedVersion?.media?.mediaUrls.en,
                original:
                  trainingResource.publishedVersion?.media?.mediaUrls.en || "",
              },
            });
          }}
          onCoverImageChange={(x) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              emojiIcon: x.emojiIcon || props.coverImage?.emojiIcon,
              background: x.background || props.coverImage?.background,
            });
          }}
          initialCoverImage={props.coverImage || undefined}
          filterResources={[LinkType.IMAGE, LinkType.EMOJI]}
          trigger={
            <Button variant="outline" className="hidden group-hover:flex">
              <BucketPaintOutlineIcon className="size-4" />
              Change cover
            </Button>
          }
        />
        {props.coverImage?.imageUrls ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setShowPinturaModal(true)}
                  className="hidden group-hover:flex"
                >
                  <CropIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit image</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  className="hidden group-hover:flex"
                  onClick={() => props.setCoverImage(undefined)}
                >
                  <TrashOutlineIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove image</TooltipContent>
            </Tooltip>
          </>
        ) : null}
      </div>
      {fullSizeCoverImage && showPinturaModal && props.coverImage?.imageUrls ? (
        <PinturaModal
          aspectRatio={16 / 9}
          onNewImageUrl={(x) => {
            props.setCoverImage({
              id: props.coverImage?.id || "",
              background: null,
              emojiIcon: null,
              mediaUrl: x,
              imageUrls: {
                id: props.coverImage?.id || "",
                medium: x,
                wide: x,
                original: x,
              },
            });
          }}
          imageUrl={fullSizeCoverImage}
          visible={showPinturaModal}
          hide={() => setShowPinturaModal(false)}
        />
      ) : null}
    </TooltipProvider>
  );
};

export default UpdateCoverImageMenu;
